import type { MetaFunction } from "@remix-run/node"

import * as UI from "@iyk/ui"
import * as React from "react"
import * as OpenGraph from "../../lib/utils/open-graph.ts"

import { DateTime } from "@iyk/date-time"
import { when } from "@iyk/flow"
import { toSentenceCase } from "@iyk/string"
import { Await, Link, useLoaderData } from "@remix-run/react"
import {
  PointsOnboardingDialog,
  usePointsOnboarding,
} from "../../components/points/points-onboarding.tsx"
import { ItemMediaGallery } from "../../components/profiles/item-media-gallery.tsx"
import { AsyncItemOwnersSection } from "../../components/profiles/item-owners-section.tsx"
import { ProfileLayout } from "../../components/profiles/layout.tsx"
import { OtherChippedItems } from "../../components/profiles/other-chipped-items.tsx"
import { ShareDialog } from "../../components/profiles/share-dialog.tsx"
import { AsyncTeamLinkWithHoverCard } from "../../components/profiles/team-link-with-hover-card.tsx"
import { ReferralAlert } from "../../components/referrals/referral-alert.tsx"
import { Shop } from "../../components/shop/shop.tsx"
import { HOW_TO_TAP_URL, SHIPPING_RETURNS_URL } from "../../lib/constants.ts"
import { useIykUser } from "../../lib/hooks/use-iyk-user.ts"
import { useLang } from "../../lib/lang/use-lang.ts"
import { CollapsibleSection } from "../../lib/ui/collapsible-section.tsx"
import { loader } from "./loader.server.ts"

export { loader }

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [{ title: "IYK" }]
  const title = `${data.item.name} | IYK`
  return [
    { title },
    ...OpenGraph.generateProperties({
      title,
      pathname: location.pathname,
      imageUrl: data.item.media?.[0]?.src,
      description: data.item.description,
      type: "website",
    }),
  ]
}

export default function ItemDetailPage() {
  const data = useLoaderData<typeof loader>()
  const lang = useLang()
  const iykUser = useIykUser()

  const avatarUrl = data.item.team.iconUrl || UI.toInitialsSVG(data.item.team.name[0])

  const { showPointsOnboardingDialog } = usePointsOnboarding()

  return (
    <ProfileLayout>
      {when(showPointsOnboardingDialog && data.hasPointsEnabled && iykUser.isSignedIn, () => (
        <PointsOnboardingDialog />
      ))}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 mb-10">
        <ItemMediaGallery mediaGallery={data.item.media} />
        <div
          data-referral={Boolean(data.referral)}
          className="flex flex-col gap-6 md:data-[referral=false]:py-6 md:data-[referral=true]:pb-6 md:col-span-5"
        >
          {data.referral && data.shopifyProduct && <ReferralAlert referral={data.referral} />}
          <div className="flex flex-col gap-2">
            <AsyncTeamLinkWithHoverCard
              team={data.item.team}
              teamOwnersCountPromise={data.teamOwnersCountPromise}
              currentUserOwnedItemsPromise={data.currentUserOwnedItemsPromise}
            />

            <div className="flex items-center justify-between">
              <span className="text-xl">{data.item.name}</span>
              <ShareDialog
                avatarUrl={avatarUrl}
                itemName={data.item.name}
                creatorName={data.item.team.name}
                type="itemDetail"
              />
            </div>

            {when(data.shopifyProduct, (shopifyProduct) => (
              <Shop.Root
                shopifyProduct={shopifyProduct.shopifyProductData}
                referral={data.referral}
              >
                <Shop.Container className="mt-1 mb-4">
                  <Shop.Price />
                  <Shop.Variants />
                  <Shop.AddToCartButton />
                </Shop.Container>
              </Shop.Root>
            ))}

            <AsyncItemOwnersSection
              team={data.item.team}
              itemOwnersPromise={data.itemOwnersPromise}
              showItemsColumn={false}
              itemName={data.item.name}
            />
          </div>

          {data.item.description !== "" && (
            <CollapsibleSection
              cutoff={400}
              key={data.item.id} // Make sure we re-render on route/item change - to re-calc min-height
            >
              <p className="text-balance whitespace-pre-line text-sm">{data.item.description}</p>
            </CollapsibleSection>
          )}

          <div>
            <p className="text-xs-upper">{toSentenceCase(lang.terms.CREATED)}</p>
            <p className="text-sm">
              {DateTime.from(data.item.createdAt).toString({
                dateStyle: "medium",
              })}
            </p>
          </div>

          {when(data.shopifyProduct, () => (
            <div className="flex flex-col gap-y-1">
              {[
                {
                  label: "Shipping and returns",
                  href: SHIPPING_RETURNS_URL,
                },
                {
                  label: "How to tap",
                  href: HOW_TO_TAP_URL,
                },
              ].map(({ label, href }) => (
                <div key={label} className="flex items-center text-gray-11">
                  <Link to={href} target="_blank" rel="noreferrer" className="text-sm">
                    {label}
                  </Link>
                  <UI.Icon.ArrowUpRight className="size-4" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <React.Suspense fallback={null}>
        <Await resolve={data.relatedItemsPromise}>
          {(relatedItems) => (
            <OtherChippedItems
              items={relatedItems}
              currentUserOwnedItemsPromise={data.currentUserOwnedItemsPromise}
            />
          )}
        </Await>
      </React.Suspense>
    </ProfileLayout>
  )
}
